import React, { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase_setup/firebase";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  TextField,
  Card,
  CardContent,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const roles = [
  "Инвеститор / собственик на соларен парк",
  "Оператор на соларни паркове",
  "O&M компания",
  "Проектант / EPC",
  "Фирма за почистване на панели",
  "Търговец / дистрибутор",
  "Друго",
];

const products = [
  {
    name: "Solar Wash Protect (SWP)",
    subtitle: "Почистване на соларни панели",
    description:
      "За професионална поддръжка на фотоволтаични системи. Запитвайте за подходящо количество и оферта според нуждите на вашия обект.",
    tag: "Почистване",
    icon: <ScienceOutlinedIcon sx={{ fontSize: 34 }} />,
  },
  {
    name: "Antistatic Solar Agent (ASA)",
    subtitle: "Антистатична обработка",
    description:
      "Решение за поддръжка на панелите между почистванията. Изпратете запитване за продукта и ще уточним подходящото количество и условия.",
    tag: "Поддръжка",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 34 }} />,
  },
];

const benefits = [
  {
    title: "Индивидуални цени според количеството",
    text: "Поискайте оферта според необходимото количество и параметрите на вашия обект.",
    icon: <BusinessCenterOutlinedIcon />,
  },
  {
    title: "Възможност за регулярни доставки",
    text: "Обсъдете периодични доставки според графика за поддръжка и потреблението ви.",
    icon: <LocalShippingOutlinedIcon />,
  },
  {
    title: "Съдействие при избора",
    text: "Споделете информация за системата и нуждите си, за да уточним подходящия продукт.",
    icon: <HandshakeOutlinedIcon />,
  },
];

const colors = {
  navy: "#203858",
  navyDeep: "#102846",
  blue: "#2f4f7f",
  yellow: "#f2d35c",
  background: "#f5f7fb",
  text: "#536477",
};

const B2BFullPageSolarHero = () => {
  const [formData, setFormData] = useState({
    company: "",
    role: "",
    contact: "",
    email: "",
    phone: "",
    product: "",
    quantity: "",
    regularDelivery: "",
    systemSize: "",
    notes: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [emailWarning, setEmailWarning] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    try {
      const inquiry = {
        company: formData.company.trim(),
        role: formData.role,
        contact: formData.contact.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        product: formData.product,
        quantity: formData.quantity.trim(),
        regularDelivery: formData.regularDelivery,
        systemSize: formData.systemSize.trim(),
        notes: formData.notes.trim(),
      };

      // Запазваме запитването във Firebase първо, за да не губим заявката,
      // ако имейл услугата временно не работи.
      await addDoc(collection(db, "b2b_inquiries"), {
        ...inquiry,
        status: "new",
        source: "b2b",
        createdAt: serverTimestamp(),
      });

      // Имейлът се изпраща през PHP на същия хостинг, не директно от браузъра.
      try {
        const emailResponse = await fetch("/api/submit-b2b.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(inquiry),
        });
        const emailResult = await emailResponse.json();
        if (!emailResponse.ok || emailResult.status !== "success") {
          throw new Error(emailResult.msg || "Email notification failed");
        }
      } catch (emailError) {
        console.error("B2B inquiry saved, but email notification failed:", emailError);
        setEmailWarning(
          "Запитването е записано във Firebase, но известието по имейл не беше изпратено. Моля, проверете по-късно във Firebase."
        );
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Неуспешно изпращане на B2B запитване:", error);
      setSubmitError(
        "Запитването не беше изпратено. Моля, опитайте отново след малко."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: colors.background, overflow: "hidden" }}>
      {/* HERO */}
      <Box
        component="section"
        sx={{
          position: "relative",
          py: { xs: 8, md: 13 },
          px: { xs: 2, md: 4 },
          color: "#fff",
          backgroundImage: `
            linear-gradient(100deg, rgba(16,40,70,0.94) 0%, rgba(32,56,88,0.82) 58%, rgba(32,56,88,0.55) 100%),
            url("/images/solar-panels-hero.avif")
          `,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Chip
            icon={<BusinessCenterOutlinedIcon />}
            label="РЕШЕНИЯ ЗА БИЗНЕС КЛИЕНТИ"
            sx={{
              mb: 3,
              px: 1,
              color: colors.yellow,
              backgroundColor: "rgba(242,211,92,0.12)",
              border: "1px solid rgba(242,211,92,0.45)",
              fontWeight: 800,
              letterSpacing: "0.04em",
              "& .MuiChip-icon": { color: colors.yellow },
            }}
          />
          <Typography
            component="h1"
            sx={{
              maxWidth: 900,
              fontSize: { xs: "2.15rem", sm: "2.8rem", md: "3.7rem" },
              lineHeight: 1.12,
              fontWeight: 850,
              letterSpacing: "-0.035em",
              mb: 3,
            }}
          >
            Препарати за професионална поддръжка на соларни паркове
          </Typography>
          <Typography
            sx={{
              maxWidth: 760,
              color: "rgba(255,255,255,0.84)",
              fontSize: { xs: "1rem", md: "1.18rem" },
              lineHeight: 1.85,
              mb: 4,
            }}
          >
            Корпоративни оферти за SWP и ASA и всички други продукти на ChemiTek Solar, индивидуални цени според
            количеството и възможност за регулярни доставки за фирми и оператори
            на фотоволтаични системи.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Button
              href="#b2b-form"
              variant="contained"
              size="large"
              sx={{
                px: 3.5,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: colors.yellow,
                color: colors.navy,
                fontWeight: 800,
                textTransform: "none",
                "&:hover": { backgroundColor: "#ffdf70" },
              }}
            >
              Поискай B2B оферта
            </Button>
            <Button
              href="#b2b-products"
              variant="outlined"
              size="large"
              sx={{
                px: 3.5,
                py: 1.5,
                borderRadius: 2,
                borderColor: "rgba(255,255,255,0.65)",
                color: "#fff",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": {
                  borderColor: colors.yellow,
                  color: colors.yellow,
                },
              }}
            >
              Разгледай продуктите
            </Button>
          </Box>
        </Container>
      </Box>

      {/* BUSINESS BENEFITS */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <Box sx={{ maxWidth: 760, mx: "auto", textAlign: "center", mb: 5 }}>
          <Typography
            component="h2"
            sx={{
              color: colors.navy,
              fontWeight: 850,
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              mb: 1.5,
            }}
          >
            B2B условия според нуждите на вашия бизнес
          </Typography>
          <Typography sx={{ color: colors.text, lineHeight: 1.8 }}>
            За оператори на соларни паркове, O&amp;M компании, EPC изпълнители и
            професионални фирми за почистване на панели.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {benefits.map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 3,
                  border: "1px solid rgba(32,56,88,0.10)",
                  boxShadow: "0 12px 34px rgba(32,56,88,0.06)",
                  backgroundColor: "#fff",
                }}
              >
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 2.5,
                    display: "grid",
                    placeItems: "center",
                    color: colors.navy,
                    backgroundColor: "rgba(242,211,92,0.28)",
                    mb: 2,
                    "& svg": { fontSize: 29 },
                  }}
                >
                  {item.icon}
                </Box>
                <Typography
                  component="h3"
                  sx={{ color: colors.navy, fontWeight: 800, mb: 1 }}
                >
                  {item.title}
                </Typography>
                <Typography sx={{ color: colors.text, lineHeight: 1.75 }}>
                  {item.text}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* PRODUCTS */}
      <Box
        id="b2b-products"
        component="section"
        sx={{
          py: { xs: 6, md: 9 },
          background: "linear-gradient(180deg, #fff 0%, #edf2f8 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", maxWidth: 760, mx: "auto", mb: 5 }}>
            <Typography
              component="h2"
              sx={{
                color: colors.navy,
                fontWeight: 850,
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                mb: 1.5,
              }}
            >
              Продукти за професионална поддръжка
            </Typography>
            <Typography sx={{ color: colors.text, lineHeight: 1.8 }}>
              Изпратете запитване за продукта и количеството, от което се
              нуждаете. Ще уточним наличните опции и индивидуалните търговски
              условия.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {products.map((product) => (
              <Grid item xs={12} md={6} key={product.name}>
                <Card
                  elevation={0}
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    height: "100%",
                    borderRadius: 4,
                    overflow: "hidden",
                    border: "1px solid rgba(32,56,88,0.10)",
                    boxShadow: "0 14px 38px rgba(32,56,88,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: { sm: 170 },
                      minHeight: { xs: 140, sm: 230 },
                      display: "grid",
                      placeItems: "center",
                      p: 3,
                      color: colors.navy,
                      background:
                        "linear-gradient(145deg, rgba(242,211,92,0.28), #f6f9fc)",
                    }}
                  >
                    {product.icon}
                  </Box>
                  <CardContent
                    sx={{
                      p: { xs: 3, md: 3.5 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      flex: 1,
                    }}
                  >
                    <Chip
                      label={product.tag}
                      size="small"
                      sx={{
                        mb: 1.5,
                        backgroundColor: "#fff3c4",
                        color: colors.navy,
                        fontWeight: 800,
                      }}
                    />
                    <Typography
                      component="h3"
                      sx={{
                        color: colors.navy,
                        fontWeight: 850,
                        fontSize: { xs: "1.25rem", md: "1.45rem" },
                        mb: 1,
                      }}
                    >
                      {product.name}
                    </Typography>
                    <Typography sx={{ color: colors.blue, fontWeight: 700, mb: 1.5 }}>
                      {product.subtitle}
                    </Typography>
                    <Typography sx={{ color: colors.text, lineHeight: 1.8, mb: 2.5 }}>
                      {product.description}
                    </Typography>
                    <Button
                      href="#b2b-form"
                      variant="contained"
                      sx={{
                        mt: "auto",
                        backgroundColor: colors.navy,
                        color: "#fff",
                        borderRadius: 2,
                        px: 2.5,
                        py: 1.2,
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": { backgroundColor: colors.blue },
                      }}
                    >
                      Запитай за продукта
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* QUOTE FORM */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
        <Box
          id="b2b-form"
          sx={{
            p: { xs: 2.5, sm: 4, md: 5 },
            borderRadius: 4,
            backgroundColor: "#fff",
            border: "1px solid rgba(32,56,88,0.10)",
            boxShadow: "0 18px 48px rgba(32,56,88,0.09)",
            scrollMarginTop: "100px",
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: colors.navy,
              fontWeight: 850,
              fontSize: { xs: "1.7rem", md: "2.3rem" },
              mb: 1.5,
            }}
          >
            Заявете индивидуална B2B оферта
          </Typography>
          <Typography sx={{ color: colors.text, lineHeight: 1.8, mb: 3.5 }}>
            Посочете основните параметри и ще разполагаме с повече информация,
            за да обсъдим подходящ продукт, количество и търговски условия.
          </Typography>

          {submitted ? (
            <Box
              role="status"
              sx={{
                p: 3,
                borderRadius: 2.5,
                backgroundColor: "#eaf6ee",
                color: "#23653d",
              }}
            >
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                Благодарим за запитването!
              </Typography>
              <Typography>
                Запитването ви беше записано във Firebase успешно. Ще използваме
                посочените данни, за да се свържем с вас относно B2B офертата.
              </Typography>
              {emailWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {emailWarning}
                </Alert>
              )}
            </Box>
          ) : (
            <>
              {submitError && (
                <Alert severity="error" sx={{ mb: 2.5 }}>
                  {submitError}
                </Alert>
              )}
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Компания"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    required
                    label="Тип клиент"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="Лице за контакт"
                    name="contact"
                    value={formData.contact}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Имейл"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Телефон"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Интерес към продукт"
                    name="product"
                    value={formData.product}
                    onChange={handleChange}
                  >
                    <MenuItem value="Solar Wash Protect (SWP)">
                      Solar Wash Protect (SWP)
                    </MenuItem>
                    <MenuItem value="Antistatic Solar Agent (ASA)">
                      Antistatic Solar Agent (ASA)
                    </MenuItem>
                    <MenuItem value="И двата продукта">И двата продукта</MenuItem>
                    <MenuItem value="Нуждая се от консултация">
                      Нуждая се от консултация
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ориентировъчно количество"
                    name="quantity"
                    placeholder="Напр. 20 литра или 4 туби"
                    value={formData.quantity}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="Интерес към регулярни доставки"
                    name="regularDelivery"
                    value={formData.regularDelivery}
                    onChange={handleChange}
                  >
                    <MenuItem value="Да">Да</MenuItem>
                    <MenuItem value="Не">Не</MenuItem>
                    <MenuItem value="Искам да обсъдим">Искам да обсъдим</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Мощност или площ на системата (по желание)"
                    name="systemSize"
                    placeholder="Напр. 2 MW или 12 000 m²"
                    value={formData.systemSize}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Допълнителна информация"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      backgroundColor: colors.yellow,
                      color: colors.navy,
                      fontWeight: 850,
                      textTransform: "none",
                      "&:hover": { backgroundColor: "#ffdf70" },
                    }}
                  >
                    {submitting ? (
                      <>
                        <CircularProgress size={19} sx={{ mr: 1, color: colors.navy }} />
                        Изпращане...
                      </>
                    ) : (
                      "Изпрати B2B запитване"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
            </>
          )}
        </Box>
      </Container>

      {/* CLOSING CTA */}
      <Box
        sx={{
          py: { xs: 4, md: 5 },
          px: 2,
          backgroundColor: colors.navy,
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h2"
            sx={{ fontSize: { xs: "1.4rem", md: "1.9rem" }, fontWeight: 850, mb: 1.5 }}
          >
            Нуждаете се от препарати за повече от един обект?
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.8 }}>
            Изпратете запитване и обсъдете индивидуална оферта и възможности за регулярни доставки.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default B2BFullPageSolarHero;
